import { BaseModel, type RelationConfig } from './base';
import { GiftCard } from './giftCard';
import { ReservationStatus } from './reservation';

export enum OrderItemType {
  reservation = 0,
  reservationCustomer = 1,
  product = 2,
  charge = 3,
  discount = 4,
  payment = 5,
  reservationService = 6,
  customerCredit = 7,
  giftCard = 8,
  service = 9,
  customerPass = 10,
  customerCourse = 11,
}

export const OrderItemTypeLabels: Record<OrderItemType, string> = {
  [OrderItemType.reservation]: 'general.reservation',
  [OrderItemType.reservationCustomer]: 'general.reservationCustomer',
  [OrderItemType.product]: 'general.product',
  [OrderItemType.charge]: 'general.charge',
  [OrderItemType.discount]: 'general.discount',
  [OrderItemType.payment]: 'general.payment',
  [OrderItemType.reservationService]: 'general.reservationService',
  [OrderItemType.customerCredit]: 'order.customerCredit',
  [OrderItemType.giftCard]: 'general.giftCard',
  [OrderItemType.service]: 'general.service',
  [OrderItemType.customerPass]: 'general.customerPass',
  [OrderItemType.customerCourse]: 'general.customerCourse',
};

export enum OrderItemPaymentType {
  custom = 0,
  cash = 1,
  customerCredit = 2,
  giftCard = 3,
  loyaltyPoints = 4,
  customerPayment = 5,
}

export const OrderItemPaymentTypeLabels: Record<OrderItemPaymentType, string> = {
  [OrderItemPaymentType.custom]: 'general.custom',
  [OrderItemPaymentType.cash]: 'order.cash',
  [OrderItemPaymentType.customerCredit]: 'order.customerCredit',
  [OrderItemPaymentType.giftCard]: 'general.giftCard',
  [OrderItemPaymentType.loyaltyPoints]: 'general.loyaltyPoints',
  [OrderItemPaymentType.customerPayment]: 'integration.label',
};

export enum OrderItemAmountType {
  fixed = 0,
  percentage = 1,
}

export const OrderItemAmountTypeLabels: Record<OrderItemAmountType, string> = {
  [OrderItemAmountType.fixed]: 'general.fixed',
  [OrderItemAmountType.percentage]: 'general.percentage',
};

export enum OrderItemDiscountType {
  fixed = 0,
  percentage = 1,
}

export const OrderItemDiscountTypeLabels: Record<OrderItemDiscountType, string> = {
  [OrderItemDiscountType.fixed]: 'general.fixed',
  [OrderItemDiscountType.percentage]: 'general.percentage',
};

export enum OrderItemRefundType {
  none = 0,
  noInteraction = 1,
  serverHeadless = 2,
  serverWithUIInteraction = 3,
  mobileNativeInteraction = 4,
}

export const OrderItemRefundTypeLabels: Record<OrderItemRefundType, string> = {
  [OrderItemRefundType.none]: 'general.none',
  [OrderItemRefundType.noInteraction]: 'api.models.orderItem.noInteraction',
  [OrderItemRefundType.serverHeadless]: 'api.models.orderItem.serverHeadless',
  [OrderItemRefundType.serverWithUIInteraction]: 'api.models.orderItem.serverWithUIInteraction',
  [OrderItemRefundType.mobileNativeInteraction]: 'api.models.orderItem.mobileNativeInteraction',
};

export enum OrderItemCustomerPaymentProcessor {
  paypal = 0,
  stripe = 1,
  izettle = 2,
  paypalHere = 3,
  square = 5,
  sumup = 6,
}

export const OrderItemCustomerPaymentProcessorLabels: Record<OrderItemCustomerPaymentProcessor, string> = {
  [OrderItemCustomerPaymentProcessor.paypal]: 'integration.paypal.label',
  [OrderItemCustomerPaymentProcessor.stripe]: 'integration.stripe.label',
  [OrderItemCustomerPaymentProcessor.izettle]: 'integration.izettle.label',
  [OrderItemCustomerPaymentProcessor.paypalHere]: 'integration.paypalHere.label',
  [OrderItemCustomerPaymentProcessor.square]: 'integration.square.label',
  [OrderItemCustomerPaymentProcessor.sumup]: 'integration.sumup.label',
};

export enum OrderItemDiscountTaxType {
  post = 0,
  pre = 1,
}

export const OrderItemDiscountTaxTypeLabels: Record<OrderItemDiscountTaxType, string> = {
  [OrderItemDiscountTaxType.post]: 'general.post',
  [OrderItemDiscountTaxType.pre]: 'general.pre',
};

export class OrderItemCustomerPayment extends BaseModel {
  processor!: OrderItemCustomerPaymentProcessor;
  integrationGuid!: string | null;

  get isPaymentProcessorSquare(): boolean {
    return this.processor === OrderItemCustomerPaymentProcessor.square || this.integrationGuid === 'b0bca8b297674792a6f4489d272b8230';
  }

  get isPaymentProcessorStripe(): boolean {
    return this.processor === OrderItemCustomerPaymentProcessor.stripe || this.integrationGuid === '29bdebaccd9e452fac2be8963d3daba7';
  }

  get isPaymentProcessorSumUp(): boolean {
    return this.integrationGuid === '8fc197200f9644808e5d30468e92959b';
  }

  get isPaymentProcessorZettle(): boolean {
    return this.integrationGuid === '0d5a2db31a7a423aa29bd915c4eb4c03';
  }
}

export class OrderItem extends BaseModel {
  idOrderItem!: number;
  type!: OrderItemType;
  amountType!: OrderItemAmountType;
  paymentType!: OrderItemPaymentType;
  discountType!: OrderItemDiscountType;
  discountTaxType!: OrderItemDiscountTaxType;
  refundType!: OrderItemRefundType;
  customerPayment!: OrderItemCustomerPayment | null;
  title!: string;
  itemName!: string;
  price!: number;
  priceWithIncludedTaxes!: number;
  discount!: number;
  tip!: number;
  quantity!: number;
  canRefund!: boolean;
  refund!: boolean;
  refundedAmount!: number;
  loyaltyEnabled!: boolean;
  taxIDs!: number[];
  hasImage!: boolean;
  images!: Record<string, string> | null;
  idCustomerPass!: number | null;
  idPaymentModel!: number | null;
  giftCard!: GiftCard | null;
  status!: number;

  static override relations(): RelationConfig {
    return {
      customerPayment: { type: OrderItemCustomerPayment },
    };
  }

  override objectID(): string | null {
    return this.idOrderItem?.toString() ?? null;
  }

  get editAllow(): boolean {
    const editableTypes = [
      OrderItemType.reservation, OrderItemType.reservationCustomer, OrderItemType.reservationService,
      OrderItemType.service, OrderItemType.product, OrderItemType.customerCredit,
      OrderItemType.giftCard, OrderItemType.customerPass, OrderItemType.discount, OrderItemType.charge,
    ];
    return editableTypes.includes(this.type);
  }

  get refundAllow(): boolean {
    return this.type === OrderItemType.payment && this.canRefund;
  }

  get deleteAllow(): boolean {
    const deletableTypes = [
      OrderItemType.reservation, OrderItemType.reservationCustomer, OrderItemType.reservationService,
      OrderItemType.service, OrderItemType.product, OrderItemType.customerCredit,
      OrderItemType.giftCard, OrderItemType.customerPass, OrderItemType.discount,
      OrderItemType.charge, OrderItemType.customerCourse,
    ];
    return deletableTypes.includes(this.type);
  }

  get editShowQuantity(): boolean {
    return this.type === OrderItemType.product || this.type === OrderItemType.charge;
  }

  get editShowAmountType(): boolean {
    return this.type === OrderItemType.discount || this.type === OrderItemType.charge;
  }

  get editShowLoyalty(): boolean {
    const loyaltyTypes = [
      OrderItemType.reservation, OrderItemType.reservationCustomer, OrderItemType.reservationService,
      OrderItemType.service, OrderItemType.product, OrderItemType.charge, OrderItemType.customerCourse,
    ];
    return loyaltyTypes.includes(this.type);
  }

  get editShowDiscount(): boolean {
    const discountTypes = [
      OrderItemType.reservation, OrderItemType.reservationCustomer, OrderItemType.reservationService,
      OrderItemType.service, OrderItemType.product, OrderItemType.charge,
      OrderItemType.customerPass, OrderItemType.giftCard, OrderItemType.customerCourse,
    ];
    return discountTypes.includes(this.type);
  }

  get editShowRemoveVoucher(): boolean {
    return this.type === OrderItemType.reservation;
  }

  get editShowTaxes(): boolean {
    const taxTypes = [
      OrderItemType.reservation, OrderItemType.reservationCustomer, OrderItemType.reservationService,
      OrderItemType.service, OrderItemType.product, OrderItemType.charge,
      OrderItemType.customerPass, OrderItemType.customerCourse,
    ];
    return taxTypes.includes(this.type);
  }

  get editShowEmployee(): boolean {
    const employeeTypes = [
      OrderItemType.customerCredit, OrderItemType.giftCard, OrderItemType.customerPass,
      OrderItemType.service, OrderItemType.product, OrderItemType.charge, OrderItemType.customerCourse,
    ];
    return employeeTypes.includes(this.type);
  }

  get discountAmount(): number {
    const price = this.isDiscountTaxTypePost ? this.priceWithIncludedTaxes : this.price;
    const discount = this.discount;

    if (discount) {
      if (this.isDiscountTypeFixed) return discount;
      if (this.isDiscountTypePercentage) return price * (discount / 100);
    }

    return 0;
  }

  get infoLabel(): string | null {
    if (this.type === OrderItemType.customerPass) {
      return '#' + this.idCustomerPass;
    }
    return null;
  }

  get discountedPrice(): number {
    return this.priceWithIncludedTaxes - this.discountAmount;
  }

  get image(): string | null {
    return this.hasImage ? this.images?.square || null : null;
  }

  get isProduct(): boolean { return this.type === OrderItemType.product; }
  get isCustomerCredit(): boolean { return this.type === OrderItemType.customerCredit; }
  get isGiftCard(): boolean { return this.type === OrderItemType.giftCard; }
  get isCustomerPass(): boolean { return this.type === OrderItemType.customerPass; }
  get isCharge(): boolean { return this.type === OrderItemType.charge; }
  get isCustomerCourse(): boolean { return this.type === OrderItemType.customerCourse; }

  get isDiscountTypeFixed(): boolean { return this.discountType === OrderItemDiscountType.fixed; }
  get isDiscountTypePercentage(): boolean { return this.discountType === OrderItemDiscountType.percentage; }
  get isAmountTypeFixed(): boolean { return this.amountType === OrderItemAmountType.fixed; }
  get isAmountTypePercentage(): boolean { return this.amountType === OrderItemAmountType.percentage; }
  get isDiscountTaxTypePre(): boolean { return this.discountTaxType === OrderItemDiscountTaxType.pre; }
  get isDiscountTaxTypePost(): boolean { return this.discountTaxType === OrderItemDiscountTaxType.post; }

  get isRefund(): boolean { return this.refund; }
  get isRefunded(): boolean { return this.refundedAmount > 0; }
  get isReservationStatusConfirmed(): boolean { return this.status === ReservationStatus.confirmed; }
  get isReservationStatusUnconfirmed(): boolean { return this.status === ReservationStatus.unconfirmed; }
  get isReservationStatusCancelled(): boolean { return this.status === ReservationStatus.cancelled; }
}
